export interface ClickTarget {
  textEntry: boolean;
  interactive: boolean;
  visitIgnore: boolean;
  ariaLabel?: string | null;
  title?: string | null;
  text?: string | null;
  testId?: string | null;
  fallback: string;
  parent?: ClickTarget | null;
}

const CHOICE_INPUTS = new Set(['button', 'submit', 'reset', 'checkbox', 'radio', 'file', 'image']);
const INTERACTIVE_ROLES = new Set(['button', 'link', 'tab', 'menuitem', 'option', 'checkbox', 'radio', 'switch']);

/** Visible control text, capped so a long label cannot blow the visit log. */
export function clipLabel(raw: string | null | undefined, max = 80): string {
  const clean = (raw ?? '').replace(/[\u0000-\u001F]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!clean || max < 1) return '';
  return clean.length <= max ? clean : `${clean.slice(0, max - 1)}…`;
}

/**
 * Label for a click, walking from the target up to the first button or link.
 * Clicks in text fields return null so typed search and passwords are not stored.
 */
export function labelFromChain(node: ClickTarget | null): string | null {
  let current = node;
  while (current) {
    if (current.visitIgnore) return null;
    if (current.textEntry) return null;
    if (current.interactive) {
      const label = clipLabel(current.ariaLabel || current.title || current.text || current.testId || current.fallback);
      return label || null;
    }
    current = current.parent ?? null;
  }
  return null;
}

export function selectChangeLabel(ariaLabel: string | null, title: string | null, optionText: string | null): string | null {
  const name = clipLabel(ariaLabel || title || '');
  const option = clipLabel(optionText || '');
  if (name && option) return clipLabel(`${name}: ${option}`);
  return option || name || null;
}

export function labelFromElement(target: EventTarget | null): string | null {
  if (typeof Element === 'undefined' || !(target instanceof Element)) return null;
  return labelFromChain(chainFromElement(target));
}

function chainFromElement(target: Element): ClickTarget {
  const nodes: ClickTarget[] = [];
  let node: Element | null = target;
  while (node) {
    const interactive = isInteractiveElement(node);
    nodes.push({
      textEntry: isTextEntryElement(node),
      interactive,
      visitIgnore: node.hasAttribute('data-visit-ignore'),
      ariaLabel: interactive ? node.getAttribute('aria-label') : null,
      title: interactive ? node.getAttribute('title') : null,
      text: interactive ? visibleText(node) : null,
      testId: interactive ? node.getAttribute('data-testid') : null,
      fallback: interactive ? fallbackName(node) : ''
    });
    node = node.parentElement;
  }
  for (let i = 0; i < nodes.length - 1; i++) nodes[i].parent = nodes[i + 1];
  return nodes[0];
}

function isTextEntryElement(node: Element): boolean {
  if (node instanceof HTMLSelectElement) return true;
  if (node instanceof HTMLTextAreaElement) return true;
  if (node instanceof HTMLInputElement) return !CHOICE_INPUTS.has(node.type.toLowerCase());
  if (node instanceof HTMLElement && node.isContentEditable && !isInteractiveElement(node)) return true;
  return false;
}

function isInteractiveElement(node: Element): boolean {
  const role = node.getAttribute('role')?.toLowerCase();
  if (role && INTERACTIVE_ROLES.has(role)) return true;
  if (node instanceof HTMLButtonElement || node instanceof HTMLAnchorElement) return true;
  if (node.tagName === 'SUMMARY' || node.tagName === 'LABEL') return true;
  if (node instanceof HTMLInputElement) return CHOICE_INPUTS.has(node.type.toLowerCase());
  return false;
}

function visibleText(node: Element): string {
  if (node instanceof HTMLInputElement) {
    const type = node.type.toLowerCase();
    if (type === 'button' || type === 'submit' || type === 'reset') return node.value;
    return '';
  }
  const text = node.textContent ?? '';
  return text.length > 200 ? text.slice(0, 200) : text;
}

function fallbackName(node: Element): string {
  if (node instanceof HTMLInputElement) return node.type.toLowerCase();
  return node.tagName.toLowerCase();
}
